import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  HardDrive, Upload, Download, Trash2, Search, Loader2, FileText,
  Image as ImageIcon, FileArchive, File as FileIcon, X, ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FilePreviewSheet, { canPreview, isImage, fileUrl } from "@/components/files/FilePreviewSheet";
import { chunkedUpload } from "@/lib/chunkedUpload";

const MAX_BYTES = 500 * 1024 * 1024;

const fmtSize = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return "—"; }
};

const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const iconFor = (ct = "", name = "") => {
  const c = ct.toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (c.startsWith("image/")) return ImageIcon;
  if (c.includes("pdf") || ["doc", "docx", "txt", "csv", "xls", "xlsx"].includes(ext)) return FileText;
  if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return FileArchive;
  return FileIcon;
};

export default function FilesManager({ projectId = null }) {
  const embedded = !!projectId;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [projects, setProjects] = useState([]);
  const [uploadProject, setUploadProject] = useState("none");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [batch, setBatch] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (projectId) params.project_id = projectId;
      const { data } = await api.get("/storage/files", { params });
      setFiles(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (e) { toast.error(apiErr(e, "Could not load files")); }
    finally { setLoading(false); }
  }, [q, projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (embedded) return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        const list = Array.isArray(data) ? data : (data?.projects || []);
        setProjects(list.map((p) => ({ id: p.project_id, name: p.name })).filter((p) => p.id));
      } catch { /* non-blocking */ }
    })();
  }, [embedded]);

  const uploadOne = async (file) => {
    const target = embedded ? projectId : (uploadProject === "none" ? null : uploadProject);
    const { data: init } = await api.post("/storage/init", {
      filename: file.name, size: file.size,
      content_type: file.type || "application/octet-stream",
      project_id: target,
    });
    const CH = init.chunk_size || 8 * 1024 * 1024;
    for (let start = 0; start < file.size; start += CH) {
      const chunk = file.slice(start, Math.min(start + CH, file.size));
      await api.put(`/storage/chunk/${init.upload_id}`, chunk,
        { headers: { "Content-Type": "application/octet-stream" } });
      setUploadPct(Math.min(99, Math.round((Math.min(start + CH, file.size) / file.size) * 100)));
    }
    await api.post(`/storage/complete/${init.upload_id}`);
  };

  const uploadMany = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (uploading || !arr.length) return;
    const valid = arr.filter((f) => {
      if (f.size <= 0) { toast.error(`"${f.name}" is empty`); return false; }
      if (f.size > MAX_BYTES) { toast.error(`"${f.name}" exceeds the 500 MB limit`); return false; }
      return true;
    });
    if (!valid.length) return;
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < valid.length; i++) {
      setBatch({ current: i + 1, total: valid.length, name: valid[i].name });
      setUploadPct(0);
      try { await uploadOne(valid[i]); ok += 1; }
      catch (err) { toast.error(`"${valid[i].name}": ${apiErr(err, "upload failed")}`); }
    }
    if (ok) { toast.success(ok > 1 ? `${ok} files uploaded` : "File uploaded"); await load(); }
    setUploading(false); setUploadPct(0); setBatch(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadMany(e.dataTransfer.files);
  };

  const downloadFiles = async (list) => {
    for (const f of list) {
      const a = document.createElement("a");
      a.href = fileUrl(f);
      a.download = f.original_filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      await new Promise((r) => setTimeout(r, 400));
    }
  };

  const removeFiles = async (list) => {
    const n = list.length;
    if (!window.confirm(n === 1
      ? `Delete "${list[0].original_filename}"? This removes it from storage.`
      : `Delete ${n} files? This removes them from storage.`)) return;
    const ids = new Set(list.map((f) => f.file_id));
    let ok = 0;
    for (const f of list) {
      try { await api.delete(`/storage/files/${f.file_id}`); ok += 1; }
      catch (e) { toast.error(`"${f.original_filename}": ${apiErr(e, "delete failed")}`); }
    }
    if (ok) {
      setFiles((prev) => prev.filter((x) => !ids.has(x.file_id)));
      setSelected(new Set());
      toast.success(ok > 1 ? `${ok} files deleted` : "File deleted");
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allChecked = files.length > 0 && selected.size === files.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(files.map((f) => f.file_id)));
  const selectedFiles = files.filter((f) => selected.has(f.file_id));

  return (
    <div data-testid="files-manager">
      {!embedded && (
        <>
          <div className="flex items-center gap-3 mb-1">
            <HardDrive className="h-6 w-6 text-zinc-900" />
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Files</h1>
          </div>
          <p className="text-sm text-zinc-500 mb-8">
            Upload and manage any file up to 500&nbsp;MB. Everything you upload here lives in one place — preview, download or delete it anytime.
          </p>
        </>
      )}

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="files-dropzone"
        className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${
          dragOver ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white"
        }`}
      >
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="files-input"
          onChange={(e) => uploadMany(e.target.files)} />
        <Upload className="h-7 w-7 mx-auto text-zinc-400 mb-2" />
        <p className="text-sm text-zinc-600 mb-4">
          {embedded ? "Drag & drop files for this project, or" : "Drag & drop files here, or"}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {!embedded && (
            <div className="w-full sm:w-64">
              <Select value={uploadProject} onValueChange={setUploadProject}>
                <SelectTrigger data-testid="files-project-select" className="h-10">
                  <SelectValue placeholder="No project (general)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project (general)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid="files-choose-button"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white h-10 px-5 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 transition-colors">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Choose files"}
          </button>
        </div>
        {uploading && batch && (
          <div className="mt-5 max-w-sm mx-auto" data-testid="files-progress">
            <p className="text-xs text-zinc-500 mb-1 truncate">Uploading {batch.current}/{batch.total}: {batch.name}</p>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-zinc-900 transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Toolbar: search + bulk actions */}
      <div className="flex items-center gap-3 mt-6 mb-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files by name…"
            data-testid="files-search" className="pl-9 h-10" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="files-bulk-bar">
            <span className="text-sm text-zinc-600">{selected.size} selected</span>
            <button onClick={() => downloadFiles(selectedFiles)} data-testid="files-bulk-download"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-200 text-sm font-medium hover:bg-zinc-100 transition-colors">
              <Download className="h-4 w-4" /> Download
            </button>
            <button onClick={() => removeFiles(selectedFiles)} data-testid="files-bulk-delete"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50 transition-colors">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
        {loading ? (
          <div className="p-10 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : files.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm" data-testid="files-empty">
            {q ? "No files match your search." : "No files yet — upload your first file above."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-200">
                <th className="pl-4 py-3 w-10">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                    data-testid="files-select-all" className="h-4 w-4 accent-zinc-900 cursor-pointer" />
                </th>
                <th className="px-4 py-3 font-semibold">Name</th>
                {!embedded && <th className="px-4 py-3 font-semibold hidden md:table-cell">Project</th>}
                <th className="px-4 py-3 font-semibold hidden sm:table-cell">Size</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Uploaded by</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Date</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => {
                const Ico = iconFor(f.content_type, f.original_filename);
                const previewable = canPreview(f);
                return (
                  <tr key={f.file_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`file-row-${f.file_id}`}>
                    <td className="pl-4 py-3">
                      <input type="checkbox" checked={selected.has(f.file_id)} onChange={() => toggle(f.file_id)}
                        data-testid={`file-check-${f.file_id}`} className="h-4 w-4 accent-zinc-900 cursor-pointer" />
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => previewable ? setPreview(f) : downloadFiles([f])}
                        data-testid={`file-name-${f.file_id}`}
                        className="flex items-center gap-2 min-w-0 text-left group">
                        {isImage(f) ? (
                          <img src={fileUrl(f, true)} alt="" loading="lazy"
                            data-testid={`file-thumb-${f.file_id}`}
                            className="h-8 w-8 rounded-sm object-cover border border-zinc-200 shrink-0 bg-zinc-50" />
                        ) : (
                          <Ico className="h-4 w-4 text-zinc-400 shrink-0" />
                        )}
                        <span className={`truncate font-medium text-zinc-800 ${previewable ? "group-hover:underline" : ""}`}>{f.original_filename}</span>
                      </button>
                    </td>
                    {!embedded && <td className="px-4 py-3 hidden md:table-cell text-zinc-500">{f.project_name || "—"}</td>}
                    <td className="px-4 py-3 hidden sm:table-cell text-zinc-500">{fmtSize(f.size)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-zinc-500">{f.uploaded_by_name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-zinc-500">{fmtDate(f.uploaded_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadFiles([f])} data-testid={`file-download-${f.file_id}`} title="Download"
                          className="h-8 w-8 flex items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors">
                          <Download className="h-4 w-4" />
                        </button>
                        <button onClick={() => removeFiles([f])} data-testid={`file-delete-${f.file_id}`} title="Delete"
                          className="h-8 w-8 flex items-center justify-center rounded-sm text-zinc-500 hover:bg-red-100 hover:text-red-700 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview side panel */}
      <FilePreviewSheet
        file={preview}
        onClose={() => setPreview(null)}
        onSaveMarkup={async (markedFile) => {
          await chunkedUpload({
            base: "/storage",
            file: markedFile,
            initBody: { project_id: preview?.project_id || projectId || null },
          });
          toast.success("Marked-up version saved");
          await load();
        }}
      />
    </div>
  );
}
